import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import treeStructureSvg from 'assets/icons/tree-structure.svg';
import { FeatureFlags } from 'components/common/types/Features.types';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import usePendoTracking from 'customHooks/usePendoTracking';

import ToggleSwitch from 'lib/ToggleSwitch';
import SvgIcon from 'lib/SvgIcon';
import { Color } from 'lib/SvgIcon/SvgIcon.types';
import { endDealViewSwitch } from 'utils/dealSwitchView';
import { getPlannerFamily } from 'utils/isPlannerPath';
import { hasPlannerOnlyFilters } from 'utils/hasPlannerOnlyFilters';
import Modal from 'lib/Modal';
import { Store } from 'components/common/types/Store.types';
import { changeFrontEndType } from 'store/dealManagement/reducer';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';
import { switchView } from './switchDealView';

const DealViewToggle = (): React.ReactElement | null => {
  const dispatch = useDispatch();
  const hasOnlyAllowPlanner = useHasFeatureAccess(FeatureFlags.ONLY_ALLOW_PLANNER);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getPendoDataId } = usePendoTracking();
  const { pathname, state: locationState } = useLocation();
  const navigate = useNavigate();
  const { campaignId, internalId } = useParams();

  const lines = useSelector(getBackupLines);
  const currentLine = useSelector(getCurrentLine);
  const plannerSecondaryPanelType = useSelector((state: Store) => state.dealManagement.plannerSecondaryPanelType);
  const openedForPlannerOnlyFilters = Boolean(
    (locationState as { hasPlannerOnlyFilters?: boolean } | null)?.hasPlannerOnlyFilters,
  );

  const isPlanner = getPlannerFamily(pathname) === 'programmatic';
  const isDealForm = pathname.includes('/deal');
  const [isToggleChecked, setIsToggleChecked] = useState(isPlanner);

  useEffect(() => {
    endDealViewSwitch();
  }, []);

  useEffect(() => {
    setIsToggleChecked(isPlanner);
  }, [isPlanner]);

  if (hasOnlyAllowPlanner) return null;

  const isBlocked = isPlanner && hasPlannerOnlyFilters(lines, currentLine);

  if (!isPlanner && !isDealForm) return null;

  const id = campaignId || internalId;
  const isSecondaryPanelOpen = isPlanner && plannerSecondaryPanelType !== PlannerSecondaryPanelType.NONE;
  const viewStatusHeading = isPlanner ? 'You are now in the new experience' : 'You are in the deal form';

  const onSwitchView = (): void => {
    if (!isBlocked) {
      setIsToggleChecked(!isPlanner);
    }

    switchView({
      isBlocked,
      isPlanner,
      id,
      navigate,
      openModal: () => setIsModalOpen(true),
      frontEndType: currentLine.frontEndType,
      onChangeFrontEndType: (frontEndType) => dispatch(changeFrontEndType(frontEndType)),
    });
  };

  const closeModal = (): void => {
    setIsModalOpen(false);

    if (openedForPlannerOnlyFilters) {
      navigate(pathname, { replace: true, state: null });
    }
  };

  return (
    <div data-test-id="deal-view-toggle" className="mb-1 flex flex-col gap-2 min-w-0">
      <p className="mx-1 text-sm font-semibold text-neutral-950" data-test-id="deal-view-status">
        {viewStatusHeading}
      </p>

      <div className="pointer-events-auto flex min-w-0 items-center gap-1 rounded-[12px] bg-neutral-50 px-2 py-1 shadow-sm">
        <span
          className="sub-header-base flex flex-1 items-center gap-1 text-neutral-950"
          title="Try the deal creation experience"
        >
          <SvgIcon color={Color.SECONDARY} src={treeStructureSvg} /> Try the deal creation experience
        </span>
        <Tooltip
          tooltip={isSecondaryPanelOpen && 'Unable to switch whilst your changes are unsaved'}
          direction={TooltipDirection.BOTTOM_LEFT}
        >
          <ToggleSwitch
            isChecked={isToggleChecked}
            isDisabled={isSecondaryPanelOpen}
            onChange={onSwitchView}
            dataPendoId={getPendoDataId('Try the deal creation experience')}
            ddActionName="Try the deal creation experience"
          />
        </Tooltip>
      </div>
      <Modal
        isOpen={isModalOpen || openedForPlannerOnlyFilters}
        onClose={closeModal}
        title="Can't switch to the deal form"
        text="This deal cannot be opened in the legacy deal form, as it uses features the form cannot edit."
      />
    </div>
  );
};

export default DealViewToggle;
